/**
 * LiveSession — Cliente WebSocket para sesiones sincronizadas profesor-alumno.
 *
 * Conecta al backend /ws/sessions/{roomId}?token=<JWT>. Si el usuario es
 * teacher, emite eventos `presenter.*` al resto de la sala. Si es student,
 * recibe y los aplica al estado local (p.ej. cargar el caso que el profe
 * carga).
 *
 * Reconexión con backoff exponencial (simple: 2x hasta 30s). Auto-desconecta
 * al logout o al navegar fuera de la página.
 */

import { authService } from '../auth/AuthService';

export type LiveEvent =
  | { type: 'presenter.caseLoaded'; caseId: string; from_name?: string }
  | { type: 'presenter.gesture'; gesture: string; nodeId?: string; from_name?: string }
  | { type: 'presenter.annotation'; text: string; from_name?: string }
  // Sprint 5: feed del monitor del alumno → profesor que mira en vivo.
  | { type: 'student.caseStart'; caseId: string | null; diagnosis: string | null; chiefComplaint: string | null; from_user_id?: number; from_name?: string; from_role?: string }
  | { type: 'student.action'; label: string; detail?: string; from_user_id?: number; from_name?: string; from_role?: string }
  | { type: 'student.drug'; name: string; dose: string; from_user_id?: number; from_name?: string; from_role?: string }
  | { type: 'student.gesture'; gesture: string; data?: Record<string, unknown>; from_user_id?: number; from_name?: string; from_role?: string }
  | { type: 'student.voice'; role: 'student' | 'patient'; text: string; from_user_id?: number; from_name?: string; from_role?: string }
  | { type: 'student.vitals'; vitals: Record<string, number | null>; from_user_id?: number; from_name?: string; from_role?: string }
  | { type: 'student.alarm'; level: string; message: string; from_user_id?: number; from_name?: string; from_role?: string }
  | { type: 'student.caseEnd'; from_user_id?: number; from_name?: string; from_role?: string }
  | { type: 'system.peerJoined'; user_id: number; display_name: string; role: string }
  | { type: 'system.peerLeft'; user_id: number }
  | { type: 'system.roster'; peers: Array<{ user_id: number; display_name: string; role: string }> };

type Listener = (event: LiveEvent) => void;

class LiveSessionClient {
  private ws: WebSocket | null = null;
  private roomId: string | null = null;
  private listeners = new Set<Listener>();
  private reconnectDelay = 2000;
  private shouldReconnect = false;
  private reconnectTimer: number | null = null;

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  get currentRoom(): string | null {
    return this.roomId;
  }

  onEvent(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  connect(roomId: string): void {
    if (!authService.isAuthenticated) {
      console.warn('[LiveSession] no autenticado, no conecto');
      return;
    }
    this.disconnect();
    this.roomId = roomId;
    this.shouldReconnect = true;
    this.reconnectDelay = 2000;
    this.openSocket();
  }

  disconnect(): void {
    this.shouldReconnect = false;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try { this.ws.close(); } catch { /* ignore */ }
      this.ws = null;
    }
    this.roomId = null;
  }

  send(event: LiveEvent): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(event));
  }

  private openSocket(): void {
    const apiBase = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000').replace(/\/$/, '');
    const wsBase = apiBase.replace(/^http/, 'ws');
    const token = localStorage.getItem('cgt_token');
    if (!token || !this.roomId) return;

    const url = `${wsBase}/ws/sessions/${encodeURIComponent(this.roomId)}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.addEventListener('open', () => {
      console.log(`[LiveSession] conectado a sala ${this.roomId}`);
      this.reconnectDelay = 2000;
    });

    ws.addEventListener('message', (ev) => {
      try {
        const event = JSON.parse(ev.data) as LiveEvent;
        this.listeners.forEach(fn => fn(event));
      } catch {
        console.warn('[LiveSession] mensaje no-JSON recibido');
      }
    });

    ws.addEventListener('close', () => {
      this.ws = null;
      if (!this.shouldReconnect) return;
      const delay = Math.min(this.reconnectDelay, 30000);
      console.info(`[LiveSession] cerrado; reintentar en ${delay}ms`);
      this.reconnectTimer = window.setTimeout(() => this.openSocket(), delay);
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
    });

    ws.addEventListener('error', (e) => {
      console.warn('[LiveSession] error', e);
    });
  }
}

export const liveSession = new LiveSessionClient();
